import { OpenAI } from "@lobehub/icons";
import { Button } from "@promptwatch/ui/components/button";
import { cn } from "@promptwatch/ui/lib/utils";
import { format } from "date-fns";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import type { GetStaticPaths, GetStaticProps } from "next";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { usePostHog } from "posthog-js/react";
import type { ComponentProps } from "react";
import ReactMarkdown from "react-markdown";
import { EmailShareButton, FacebookShareButton, LinkedinShareButton, TwitterShareButton } from "react-share";
import rehypeSlug from "rehype-slug";
import remarkGfm from "remark-gfm";
import BaseLayout from "@/components/base-layout";
import { Header } from "@/components/header";
import { clientEnv } from "@/env/client";
import type { BlogPost } from "@/lib/markdown";
import { getAllBlogPosts, getBlogPostBySlug } from "@/lib/markdown";
import TableOfContents from "../../components/table-of-contents";

// Define Author details
// TODO: Replace placeholder URLs with actual profile links
export const authors = {
  gijs: {
    name: "Gijs de Groot",
    image: "/gijs.jpeg",
    title: "Co-Founder & CEO",
    about:
      "Gijs is the Co-Founder of Promptwatch, focusing on AI Search Optimization (GEO). He helps brands improve their visibility in AI-driven search platforms.",
    url: undefined, // Optional: Add URL to a bio page on promptwatch.com
    sameAs: ["https://www.linkedin.com/in/gijs-de-groot2021"],
  },
  klaas: {
    name: "Klaas Foppen",
    title: "Co-Founder & CTO",
    about:
      "Klaas is the Co-Founder & CTO of Promptwatch, focusing on AI Search Optimization with extensive experience in software engineering and AI.",
    url: undefined, // Optional: Add URL to a bio page on promptwatch.com
    image: "/klaas.jpg",
    sameAs: ["https://www.linkedin.com/in/klaas-foppen", "https://x.com/forgebitz"],
  },
};

interface BlogPostPageProps {
  post: BlogPost;
}

const BlogPostPage = ({ post }: BlogPostPageProps) => {
  const router = useRouter();
  const posthog = usePostHog();
  // If the page is still being generated, show a loading state
  if (router.isFallback) {
    return (
      <BaseLayout>
        <main className="overflow-hidden duration-150">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-200/50 via-blue-100/50" />
          <div
            aria-hidden="true"
            className="absolute h-full w-full scale-[0.8] bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#fff_70%,transparent_100%)]"
          />
          <section className="relative">
            <div className="relative mx-auto max-w-6xl border-x border-b border-gray-950/[0.07] px-3 pb-16 pt-16">
              <p className="text-lg text-center">loading...</p>
            </div>
          </section>
        </main>
      </BaseLayout>
    );
  }

  // If the post doesn't exist, show a 404
  if (!post) {
    return (
      <BaseLayout>
        <main className="overflow-hidden duration-150">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-200/50 via-blue-100/50" />
          <div
            aria-hidden="true"
            className="absolute h-full w-full scale-[0.8] bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#fff_70%,transparent_100%)]"
          />
          <section className="relative">
            <div className="relative mx-auto max-w-6xl border-x border-b border-gray-950/[0.07] px-3 pb-16 pt-16">
              <h1 className="text-title text-center text-4xl font-medium mb-4">post not found</h1>
              <p className="text-lg text-center text-muted-foreground">the blog post you're looking for doesn't exist.</p>
              <div className="flex justify-center mt-8">
                <Link href="/blog" className="text-primary hover:underline">
                  ← back to blog
                </Link>
              </div>
            </div>
          </section>
        </main>
      </BaseLayout>
    );
  }

  const shareUrl = `https://promptwatch.com/blog/${post.slug}`;
  const shareTitle = post.title;

  // Look up the author details
  const authorDetails = post.author ? authors[post.author as keyof typeof authors] : undefined;

  return (
    <BaseLayout>
      <Header
        canonical={`https://promptwatch.com/blog/${post.slug}`}
        metaImage={`https://promptwatch.com${post.coverImage}`}
        title={`${post.title} | Promptwatch.com`}
        description={post.excerpt}
        ogType="article"
        publishedTime={post.date}
        author={authorDetails}
      />
      <main className="duration-150">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-200/50 via-blue-100/50" />
        <div
          aria-hidden="true"
          className="absolute h-full w-full scale-[0.8] bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#fff_70%,transparent_100%)]"
        />

        <section className="relative border-b border-gray-950/[0.07]" id="platform">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11 z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11 z-40" />
            <div className="relative border-x border-gray-950/[0.07]">
              <nav aria-label="Breadcrumb" className="p-3">
                <ol className="flex items-center text-sm">
                  <li>
                    <Link href="/blog" className="text-primary hover:underline">
                      Blog
                    </Link>
                  </li>
                  <li className="mx-2 text-muted-foreground">/</li>
                  <li className="text-gray-700 truncate max-w-[350px]" aria-current="page">
                    {post.title}
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </section>

        <section className="relative border-b">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
            <div className="relative border-x border-gray-950/[0.07] bg-white">
              {post.coverImage && (
                <div className="mb-8 overflow-hidden border-b relative">
                  <Image
                    src={post.coverImage}
                    alt={post.title}
                    width={600}
                    height={280}
                    loading="eager"
                    className="w-full h-80 md:h-96 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white px-5 sm:px-12">
                    <h1 className="text-title text-2xl md:text-4xl font-semibold mb-2">{post.title}</h1>
                    <p className="mb-4 text-sm md:text-base hidden md:block">{post.excerpt}</p>
                    <div className="flex items-center text-sm text-white/80 mb-2 mt-2">
                      {authorDetails && (
                        <>
                          <div className="flex items-center space-x-2 ">
                            <Image src={authorDetails.image} alt={authorDetails.name} width={24} height={24} className="rounded-full" />
                            <span>{authorDetails.name}</span>
                          </div>
                          <span className="mx-2">•</span>
                        </>
                      )}
                      <time dateTime={post.date}>{format(new Date(post.date), "MMMM d, yyyy")}</time>
                    </div>
                  </div>
                </div>
              )}

              <header className="px-5 sm:px-20">
                {!post.coverImage && (
                  <>
                    <h1 className="text-title text-4xl font-semibold mb-2 ">{post.title}</h1>
                    <div className="flex items-center text-sm text-muted-foreground mb-4">
                      {authorDetails && (
                        <>
                          <span className="mx-2">•</span>
                          <div className="flex items-center space-x-2">
                            <Image src={authorDetails.image} alt={authorDetails.name} width={24} height={24} className="rounded-full" />
                            <span>{authorDetails.name}</span>
                          </div>
                        </>
                      )}
                      <time dateTime={post.date}>{format(new Date(post.date), "MMMM d, yyyy")}</time>
                    </div>
                  </>
                )}
              </header>

              {/* Layout container for article and desktop ToC */}
              <div className="flex flex-col lg:flex-row px-5 sm:px-12 pb-12 min-w-0">
                <p className="mb-4 text-sm md:text-base block md:hidden">{post.excerpt}</p>

                {/* Mobile ToC (Rendered on top for small screens) */}
                <aside className="block lg:hidden mb-8">
                  <TableOfContents headings={post.headings || []} />
                  {/* Mobile Author Section */}
                </aside>

                <article className="max-w-none prose prose-headings:first:mt-0 lg:flex-[3] lg:pr-12 min-w-0">
                  <a
                    href={`https://chatgpt.com?prompt=${encodeURIComponent(`Read from https://promptwatch.com/blog/${post.slug} so I can ask questions about it.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-4 my-3 px-2 py-2 rounded-lg bg-white border border-gray-200 text-gray-900 shadow-lg transition-colors duration-150 hover:bg-gray-50 no-underline hover:no-underline"
                  >
                    <div className="flex-shrink-0 w-12 h-12 rounded-md bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                      <OpenAI className="w-7 h-7 text-black" />
                    </div>
                    <div className="flex flex-col items-start">
                      <span className="text-lg font-semibold">Open in ChatGPT</span>
                      <span className="text-sm text-gray-600">Ask questions about this article</span>
                    </div>
                    <ArrowUpRight className="w-6 h-6 flex-shrink-0" />
                  </a>

                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeSlug]}
                    components={{
                      pre: (props: ComponentProps<"pre">) => (
                        // Style the container for code blocks with proper mobile overflow handling
                        <div className="overflow-x-auto my-6 -mx-2 sm:mx-0">
                          <pre
                            className="!max-w-none !overflow-x-visible !whitespace-pre bg-gray-900 text-gray-100 p-4 rounded-md text-sm sm:text-base min-w-full"
                            {...props}
                          />
                        </div>
                      ),
                      table: (props: ComponentProps<"table">) => (
                        <div className="overflow-x-auto my-4 w-full">
                          <table className="border-collapse border border-gray-300" style={{ width: "auto" }} {...props} />
                        </div>
                      ),
                      th: (props: ComponentProps<"th">) => (
                        <th
                          className="border border-gray-300 bg-gray-100 px-4 py-2 whitespace-nowrap"
                          style={{ width: "auto" }}
                          {...props}
                        />
                      ),
                      td: (props: ComponentProps<"td">) => (
                        <td className="border border-gray-300 px-4 py-2 whitespace-nowrap" style={{ width: "auto" }} {...props} />
                      ),
                      code: ({ inline, className, children, ...props }: ComponentProps<"code"> & { inline?: boolean }) => {
                        const blockClasses = "block break-all sm:break-normal overflow-x-auto"; // Better mobile handling for block code
                        const inlineClasses =
                          "inline-block !bg-gray-100 !px-1.5 !py-0.5 !rounded !text-sm !font-mono !align-baseline break-all sm:break-normal max-w-full";

                        if (inline) {
                          return (
                            <code className={cn(inlineClasses, className)} {...props}>
                              {children}
                            </code>
                          );
                        }

                        // Apply block styles with proper overflow handling
                        return (
                          <code className={cn(blockClasses, className)} {...props}>
                            {children}
                          </code>
                        );
                      },
                      // Add img component to ensure images are responsive
                      img: (props: ComponentProps<"img">) => <img className="max-w-full h-auto rounded-md my-4" {...props} />,
                      // Handle long URLs and links
                      a: (props: ComponentProps<"a">) => <a className="break-words hyphens-auto" {...props} />,
                    }}
                  >
                    {post.content}
                  </ReactMarkdown>

                  {/* Share buttons below article */}
                  <div className="mt-8 pt-6 border-t">
                    <h3 className="text-sm font-semibold mb-3 text-gray-700">Share this article</h3>
                    <div className="flex space-x-4">
                      <TwitterShareButton url={shareUrl} title={shareTitle}>
                        <div className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                          <svg className="size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <path
                              fill="currentColor"
                              d="M10.488 14.651L15.25 21h7l-7.858-10.478L20.93 3h-2.65l-5.117 5.886L8.75 3h-7l7.51 10.015L2.32 21h2.65zM16.25 19L5.75 5h2l10.5 14z"
                            />
                          </svg>
                        </div>
                      </TwitterShareButton>

                      <LinkedinShareButton url={shareUrl} title={shareTitle}>
                        <div className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                          <svg className="size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <path
                              fill="currentColor"
                              d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"
                            />
                          </svg>
                        </div>
                      </LinkedinShareButton>

                      <FacebookShareButton url={shareUrl}>
                        <div className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                          <svg className="size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <path
                              fill="currentColor"
                              d="M14 13.5h2.5l1-4H14v-2c0-1.03 0-2 2-2h1.5V2.14c-.326-.043-1.557-.14-2.857-.14C11.928 2 10 3.657 10 6.7v2.8H7v4h3V22h4v-8.5z"
                            />
                          </svg>
                        </div>
                      </FacebookShareButton>

                      <EmailShareButton url={shareUrl} subject={shareTitle} body={`Check out this article: ${shareTitle}`}>
                        <div className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                          <svg className="size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <path
                              fill="currentColor"
                              d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                            />
                          </svg>
                        </div>
                      </EmailShareButton>
                    </div>
                  </div>
                </article>

                {/* Desktop ToC (Rendered on the right for large screens) */}
                <aside className="hidden lg:block lg:flex-[1] lg:pt-0 lg:sticky lg:top-24 lg:self-start">
                  <TableOfContents headings={post.headings || []} />

                  {authorDetails && (
                    <div className="mt-8 pt-6 border-t">
                      <h3 className="text-sm font-semibold mb-3 text-gray-700">About the Author</h3>
                      <div className="flex items-start space-x-3">
                        <Image
                          src={authorDetails.image}
                          alt={authorDetails.name}
                          width={40}
                          height={40}
                          className="rounded-full flex-shrink-0"
                        />
                        <div className="flex-grow">
                          <div className="flex-col items-baseline">
                            {authorDetails.sameAs && authorDetails.sameAs.length > 0 ? (
                              <Link
                                href={authorDetails.sameAs[0]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-semibold text-sm text-gray-800 hover:underline"
                              >
                                {authorDetails.name}
                              </Link>
                            ) : (
                              <p className="font-semibold text-sm text-gray-800">{authorDetails.name}</p>
                            )}
                            {authorDetails.title && <p className="text-xs text-gray-500">{authorDetails.title}</p>}
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mt-4">{authorDetails.about}</p>
                    </div>
                  )}

                  {/* Share buttons under ToC on large screens */}
                  <div className="mt-8 pt-6 border-t">
                    <h3 className="text-sm font-semibold mb-3 text-gray-700">Share this article</h3>
                    <div className="flex space-x-4">
                      <TwitterShareButton url={shareUrl} title={shareTitle}>
                        <div className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                          <svg className="size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <path
                              fill="currentColor"
                              d="M10.488 14.651L15.25 21h7l-7.858-10.478L20.93 3h-2.65l-5.117 5.886L8.75 3h-7l7.51 10.015L2.32 21h2.65zM16.25 19L5.75 5h2l10.5 14z"
                            />
                          </svg>
                        </div>
                      </TwitterShareButton>

                      <LinkedinShareButton url={shareUrl} title={shareTitle}>
                        <div className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                          <svg className="size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <path
                              fill="currentColor"
                              d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"
                            />
                          </svg>
                        </div>
                      </LinkedinShareButton>

                      <FacebookShareButton url={shareUrl}>
                        <div className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                          <svg className="size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <path
                              fill="currentColor"
                              d="M14 13.5h2.5l1-4H14v-2c0-1.03 0-2 2-2h1.5V2.14c-.326-.043-1.557-.14-2.857-.14C11.928 2 10 3.657 10 6.7v2.8H7v4h3V22h4v-8.5z"
                            />
                          </svg>
                        </div>
                      </FacebookShareButton>

                      <EmailShareButton url={shareUrl} subject={shareTitle} body={`Check out this article: ${shareTitle}`}>
                        <div className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
                          <svg className="size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <path
                              fill="currentColor"
                              d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                            />
                          </svg>
                        </div>
                      </EmailShareButton>
                    </div>
                  </div>
                </aside>
              </div>
            </div>

            <div className="px-5 sm:px-12 border-x border-t border-gray-950/[0.07] bg-white hover:!bg-ui relative p-8 shadow-lg shadow-gray-900/5 before:absolute before:inset-1 before:rounded-b-lg before:bg-gradient-to-t before:from-blue-200 group-hover:bg-transparent">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div className="mb-6 md:mb-0  z-40">
                  <h3 className="text-xl font-semibold mb-2">Boost your brand's visibility in AI search</h3>
                  <p className="text-gray-800/80 max-w-xl">
                    Get your brand mentioned by ChatGPT, Claude, Perplexity and other AI search engines, monitor mentions and ensure your
                    business stays top of mind.
                  </p>
                </div>
                <div className="flex flex-row gap-4 z-40">
                  <Button
                    variant="outline"
                    onClick={() => {
                      posthog.capture("book_demo_button_clicked", {
                        source: "blog",
                      });
                      router.push("/book-a-demo");
                    }}
                  >
                    Book a Demo
                  </Button>
                  <Button
                    className="flex items-center gap-2"
                    onClick={() => {
                      posthog.capture("get_started_button_clicked", {
                        source: "blog",
                      });
                      router.push(clientEnv.NEXT_PUBLIC_APP_URL);
                    }}
                  >
                    Get Started <ArrowRight className="size-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </BaseLayout>
  );
};

export default BlogPostPage;

export const getStaticPaths: GetStaticPaths = async () => {
  const posts = getAllBlogPosts();

  const paths = posts.map((post) => ({
    params: { slug: post.slug },
  }));

  return {
    paths,
    fallback: true, // Enable fallback for new posts
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = params?.slug as string;
  const post = getBlogPostBySlug(slug);

  if (!post) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      post,
    },
    revalidate: 60 * 60, // Revalidate every hour
  };
};
